/** Company Analytics — v1 market-backfill.js analyticsAgg + analytics.js
 * panel helpers (_anlLeadFlow, _anlClosingRisk, anlLoadOutreach), verbatim
 * math. Everything computes CLIENT-SIDE from data v2 already holds — the
 * same posture as v1, which aggregates its in-memory DATA/TXN_COLS/WTXNS.
 *
 * Deliberate v1 quirks preserved (they are what Tom's numbers mean today):
 * - "Pipeline Value" = Σ asking price over ALL leads (yes, $1.4B).
 * - Revenue months are bucketed by ROW INDEX (idx % months), not by date —
 *   only expenses use real months. Verbatim.
 * - "Avg ROI" reads a lead-level roi that scraped rows never carry → 0.0%
 *   live in v1 and v2 both. (fields.roi is honored if it ever appears.)
 * - flipN in Business Mix = the LEAD count (v1 counts props, not deals).
 * - the outreach caption says "last 200 messages" while v1's fetch actually
 *   pages 1000 (live tiles: 607+393 = exactly 1000). Caption kept verbatim,
 *   window matched to the real behavior (most recent 1000). */
import type { Lead } from "../../types";
import { moneyExact } from "../../lib/money";
import type { Deal } from "../deals/api";
import { STAGE_NAMES } from "../deals/api";
import type { WholesaleDeal } from "../wholesale/api";
import { wtMoney } from "../wholesale/api";
import { isTaskDone, type EditsMap, type TaskRec } from "../edits/api";
import type { Comm } from "../inbox/api";
import { estMoneyNeeded } from "../roi/estMoney";
import type { NrInputs } from "../roi/math";
import { natKey } from "../../lib/natkey";
import { shortCounty } from "../convert/api";

// ---- v1 money()/roiNum() ----------------------------------------------------

/** v1 _aM → money(): strips everything but digits and dots (minus included —
 * money() has no '-' in its keep-set, so negatives read positive). */
export const anlMoney = (v: unknown): number => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const f = parseFloat(String(v ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(f) ? f : 0;
};

/** v1 roiNum(): keeps the minus sign. */
export const roiNum = (v: unknown): number => {
  const f = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(f) ? f : 0;
};

/** v1 _aProfit: ARV − price − rehab − 4.5% of ARV (selling) − 3.5% of price
 * (buying). 0 unless both ARV and price exist. */
export function anlProfit(arv: number, price: number, rehab: number): number {
  if (!arv || !price) return 0;
  return Math.round(arv - price - rehab - arv * 0.045 - price * 0.035);
}

/** v1 daysDiff (transactions.js): whole days between local midnights. */
export function daysDiff(a: Date | string | number, b: Date | string | number): number | null {
  const d1 = new Date(a);
  const d2 = new Date(b);
  if (Number.isNaN(d1.getTime()) || Number.isNaN(d2.getTime())) return null;
  return Math.round((d2.setHours(0, 0, 0, 0) - d1.setHours(0, 0, 0, 0)) / 86400000);
}

// ---- the per-lead input row ---------------------------------------------------

/** The slice of a lead the aggregates read — assembled ONCE per lead so the
 * agg never touches the fat Lead/EditsMap shapes directly. v1 parallels:
 * price=p.price, arv=p.arv, rehab=p.rehab ('$X,XXX' the rehab calculator
 * writes → v2 home is edits.rehabCalc.total), roi=p.roi (fields.roi if ever
 * set), qual=p.qual, who=p.who (v2 lead.seller), drop=p.priceDrop.pct. */
export interface AnlLead {
  price: number;
  arv: number;
  rehab: number;
  roi: number;
  cdom: number | null;
  county: string | null;
  source: string;
  createdAt: number;
  qualified: boolean;
  hasDrop: boolean;
  who: string | null;
}

export function buildAnlLeads(leads: Lead[], edits: EditsMap): AnlLead[] {
  return leads.map((l) => {
    const f = edits.get(natKey(l.address));
    let rehab = 0;
    const rc = f?.["rehabCalc"];
    if (rc !== null && typeof rc === "object" && !Array.isArray(rc)) {
      rehab = anlMoney((rc as Record<string, unknown>)["total"]);
    }
    return {
      price: l.price ?? 0,
      arv: l.arv ?? 0,
      rehab,
      roi: roiNum(f?.["roi"]),
      cdom: l.cdom,
      county: l.county,
      source: l.source,
      createdAt: l.createdAt,
      qualified: f?.["qual"] === "Qualified",
      hasDrop: l.priceDrop !== null && l.priceDrop.pct > 0,
      who: l.seller,
    };
  });
}

// ---- analyticsAgg ------------------------------------------------------------

export const ANL_RANGES: [AnlRange, string][] = [
  ["30d", "30 Days"],
  ["qtd", "Quarter"],
  ["ytd", "6 Months"],
  ["all", "12 Months"],
];
export type AnlRange = "30d" | "qtd" | "ytd" | "all";
export const rangeMonths = (r: AnlRange): number => ({ "30d": 1, qtd: 3, ytd: 6, all: 12 })[r] ?? 6;

export interface AnlMonth {
  key: string;
  label: string;
  rev: number;
  exp: number;
  profit: number;
}
export interface AnlSource {
  name: string;
  count: number;
  color: string;
  pct: number;
}
export interface AnlCounty {
  name: string; // SHORT name — the tile prints "{name} County" like v1
  count: number;
  profit: number;
}
export interface AnlExpCat {
  cat: string;
  total: number;
  color: string;
  pct: number;
}
export interface AnlTeamRow {
  name: string;
  deals: number;
  tasksDone: number;
  tasksOpen: number;
  profit: number;
}

export interface AnlAgg {
  months: AnlMonth[];
  totalProfit: number;
  pipelineVal: number;
  avgRoi: number;
  avgDays: number;
  capital: number;
  sold: number;
  active: number;
  totalDeals: number;
  whFees: number;
  whN: number;
  flipN: number;
  avgProfit: number;
  totalExp: number;
  funnel: [string, number][];
  sources: AnlSource[];
  counties: AnlCounty[];
  expCats: AnlExpCat[];
  team: AnlTeamRow[];
  buckets: [string, number, string][];
  stageRows: [string, number][];
}

const SRC_COLORS = ["#2563eb", "#10b981", "#8b5cf6", "#f59e0b", "#f43f5e", "#0ea5e9", "#f97316"];
const EC_COLORS = ["#2563eb", "#10b981", "#8b5cf6", "#f59e0b", "#f43f5e", "#0ea5e9", "#f97316", "#64748b"];

/** v1 _pipeRows: [stage name, card count] for every board column. */
export function pipeRows(deals: Deal[]): [string, number][] {
  return STAGE_NAMES.map((name, i) => [name, deals.filter((d) => d.stageIdx === i).length]);
}

/** v1 _pipeColor, verbatim. */
export const pipeColor = (n: string): string =>
  ({
    "Contract Signed / Pending": "#FCD34D",
    Closed: "#C4B5FD",
    Rehab: "#FDBA74",
    "On Market": "#93C5FD",
    "Buyer Inspection": "#7DD3FC",
    "Pending Sale": "#86EFAC",
    Sold: "#4ADE80",
  })[n] ?? "#CBD5E1";

/** v1 analyticsAgg(), verbatim math. `expenses` rows are v1's EXP2 tuples
 * [date, desc, category, amount, …] — empty today (QBO never connected). */
export function analyticsAgg(
  props: AnlLead[],
  deals: Deal[],
  wholesale: WholesaleDeal[],
  tasks: TaskRec[],
  memberNames: string[],
  expenses: unknown[][],
  range: AnlRange,
  master: NrInputs,
  now: Date,
): AnlAgg {
  const N = rangeMonths(range);
  const months: AnlMonth[] = [];
  for (let i = N - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: d.getFullYear() + "-" + d.getMonth(),
      label: d.toLocaleString("en-US", { month: "short" }),
      rev: 0,
      exp: 0,
      profit: 0,
    });
  }
  // v1: properties bucketed deterministically by INDEX across months
  props.forEach((p, idx) => {
    const b = months[idx % months.length];
    if (!b) return;
    b.rev += p.arv;
    b.profit += anlProfit(p.arv, p.price, p.rehab);
  });
  // expenses: real month when the date parses, else idx % N (v1 verbatim)
  let totalExp = 0;
  expenses.forEach((e, idx) => {
    const amt = anlMoney(e[3]);
    totalExp += amt;
    let mi = idx % months.length;
    const dt = new Date(String(e[0] ?? ""));
    if (!Number.isNaN(dt.getTime())) {
      const key = dt.getFullYear() + "-" + dt.getMonth();
      const j = months.findIndex((m) => m.key === key);
      if (j >= 0) mi = j;
    }
    const b = months[mi];
    if (b) b.exp += amt;
  });

  // DEAL-based headline KPIs (Tom 2026-08-14: "the pipeline and other things
  // here are not correct"). v1's math summed EVERY lead's asking price and
  // called it the pipeline — 3,838 leads made "Pipeline Value" read $1.2B and
  // "Capital Deployed" $52M while the sidebar's deal-based pipeline read $1.3M.
  // The portfolio cards now read the DEALS, with the sidebar's exact
  // t_purchase definition; lead-wide stats (lead flow, sources, funnel counts,
  // avg days on market) stay lead-based on purpose.
  const dMoney = (v: unknown): number => {
    const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };
  const dealPurchase = (d: Deal): number => dMoney(d.fields["t_purchase"]);
  const dealArv = (d: Deal): number => dMoney(d.fields["t_arv"]);
  const pipelineVal = deals.reduce((s, d) => s + dealPurchase(d), 0);
  const totalProfit = deals.reduce(
    (s, d) => s + anlProfit(dealArv(d), dealPurchase(d), d.rehab || 0), 0);
  const dRois = deals
    .map((d) => {
      const cost = dealPurchase(d) + (d.rehab || 0);
      if (!(cost > 0) || !(dealArv(d) > 0)) return NaN;
      return (anlProfit(dealArv(d), dealPurchase(d), d.rehab || 0) / cost) * 100;
    })
    .filter((v) => Number.isFinite(v));
  const avgRoi = dRois.length ? dRois.reduce((a, b) => a + b, 0) / dRois.length : 0;
  const cdoms = props.map((p) => (p.cdom !== null ? Math.trunc(p.cdom) : NaN)).filter((v) => v > 0);
  const avgDays = cdoms.length ? Math.round(cdoms.reduce((a, b) => a + b, 0) / cdoms.length) : 0;
  // capital: cash-to-close of the ACTIVE deals (not sold, and not every lead)
  const soldIdx0 = STAGE_NAMES.indexOf("Sold");
  const capital = deals
    .filter((d) => d.stageIdx !== soldIdx0)
    .reduce((s, d) => s + estMoneyNeeded(dealPurchase(d), d.rehab || 0, null, master), 0);

  const stageRows = pipeRows(deals);
  const pipe = (name: string): number => stageRows.find((r) => r[0] === name)?.[1] ?? 0;
  const sold = pipe("Sold");
  const totalDeals = stageRows.reduce((s, r) => s + r[1], 0) || props.length; // v1 fallback
  const active = Math.max(totalDeals - sold /* − Cancelled: no such column */, 0);
  const whFees = wholesale.reduce((s, w) => s + wtMoney(w.fee), 0);
  // per-flip average over the DEALS the profit was computed from (was ÷3,838 leads)
  const avgProfit = deals.length ? Math.round(totalProfit / deals.length) : 0;

  const funnel: [string, number][] = [
    ["Leads", props.length],
    ["Contract Signed", pipe("Contract Signed / Pending")],
    ["Rehab", pipe("Rehab")],
    ["On Market", pipe("On Market")],
    ["Pending Sale", pipe("Pending Sale")],
    ["Sold", sold],
  ];

  const srcMap: Record<string, number> = {};
  props.forEach((p) => {
    const s = p.source || "Investorlift";
    srcMap[s] = (srcMap[s] ?? 0) + 1;
  });
  const sources: AnlSource[] = Object.keys(srcMap).map((k, i) => ({
    name: k,
    count: srcMap[k] ?? 0,
    color: SRC_COLORS[i % SRC_COLORS.length] ?? "#2563eb",
    pct: 0,
  }));
  const srcTot = sources.reduce((s, x) => s + x.count, 0) || 1;
  sources.forEach((s) => (s.pct = Math.round((s.count / srcTot) * 100)));
  sources.sort((a, b) => b.count - a.count);

  const ctyMap: Record<string, { count: number; profit: number }> = {};
  props.forEach((p) => {
    if (!p.county) return;
    const k = shortCounty(p.county) || p.county;
    const e = (ctyMap[k] = ctyMap[k] ?? { count: 0, profit: 0 });
    e.count++;
    e.profit += anlProfit(p.arv, p.price, p.rehab);
  });
  const counties: AnlCounty[] = Object.keys(ctyMap)
    .map((k) => ({ name: k, count: ctyMap[k]?.count ?? 0, profit: ctyMap[k]?.profit ?? 0 }))
    .sort((a, b) => b.profit - a.profit)
    .slice(0, 6);

  const ecMap: Record<string, number> = {};
  expenses.forEach((e) => {
    const c = String(e[2] ?? "") || "Other";
    ecMap[c] = (ecMap[c] ?? 0) + anlMoney(e[3]);
  });
  const expCats: AnlExpCat[] = Object.keys(ecMap)
    .map((k, i) => ({ cat: k, total: ecMap[k] ?? 0, color: EC_COLORS[i % EC_COLORS.length] ?? "#2563eb", pct: 0 }))
    .sort((a, b) => b.total - a.total);
  const ecTot = expCats.reduce((s, x) => s + x.total, 0) || 1;
  expCats.forEach((x) => (x.pct = Math.round((x.total / ecTot) * 100)));

  const users = memberNames.length ? memberNames : ["Tom"];
  const team: AnlTeamRow[] = users
    .map((u) => {
      const mine = props.filter((p) => (p.who ?? "Tom") === u);
      let td = 0;
      let to = 0;
      for (const t of tasks) {
        if ((t.who ?? "Tom") === u) {
          if (isTaskDone(t.status)) td++;
          else to++;
        }
      }
      return {
        name: u,
        deals: mine.length,
        tasksDone: td,
        tasksOpen: to,
        profit: mine.reduce((s, p) => s + anlProfit(p.arv, p.price, p.rehab), 0),
      };
    })
    .sort((a, b) => b.profit - a.profit);

  const buckets: [string, number, string][] = [
    ["<0%", 0, "#dc2626"],
    ["0–10%", 0, "#f59e0b"],
    ["10–15%", 0, "#f59e0b"],
    ["15–20%", 0, "#10b981"],
    ["20%+", 0, "#059669"],
  ];
  props.forEach((p) => {
    const r = p.roi;
    if (!r) return; // v1 skips 0 (and unset)
    if (r < 0) buckets[0]![1]++;
    else if (r < 10) buckets[1]![1]++;
    else if (r < 15) buckets[2]![1]++;
    else if (r < 20) buckets[3]![1]++;
    else buckets[4]![1]++;
  });

  return {
    months, totalProfit, pipelineVal, avgRoi, avgDays, capital, sold, active,
    totalDeals, whFees, whN: wholesale.length, flipN: props.length, avgProfit,
    totalExp, funnel, sources, counties, expCats, team, buckets, stageRows,
  };
}

// ---- Lead Flow (v1 _anlLeadFlow) ----------------------------------------------

export interface LeadFlowDay {
  t: number;
  label: string;
  n: number;
}
export interface LeadFlow {
  days: LeadFlowDay[];
  today: number;
  yesterday: number;
  week: number;
  qualified: number;
  total: number;
  drops: number;
  maxD: number;
}

export function leadFlow(props: AnlLead[], now: Date): LeadFlow {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const days: LeadFlowDay[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    days.push({ t: d.getTime(), label: d.toLocaleDateString("en-US", { day: "numeric" }), n: 0 });
  }
  let t0 = 0;
  let t1 = 0;
  let w = 0;
  for (const p of props) {
    if (!p.createdAt) continue;
    const x = new Date(p.createdAt);
    x.setHours(0, 0, 0, 0);
    const t = x.getTime();
    const d = days.find((y) => y.t === t);
    if (d) d.n++;
    if (t === today.getTime()) t0++;
    if (t === today.getTime() - 86400000) t1++;
    if (t >= today.getTime() - 6 * 86400000) w++;
  }
  return {
    days,
    today: t0,
    yesterday: t1,
    week: w,
    qualified: props.filter((p) => p.qualified).length,
    total: props.length,
    drops: props.filter((p) => p.hasDrop).length,
    maxD: Math.max(1, ...days.map((d) => d.n)),
  };
}

// ---- Closing Risk (v1 _anlClosingRisk) -----------------------------------------

export interface RiskRow {
  address: string;
  close: string;
  dtc: number | null;
  open: number;
  tone: "overdue" | "urgent" | "ok";
}

export function closingRisk(deals: Deal[], tasks: TaskRec[], now: Date): RiskRow[] {
  const rows: RiskRow[] = [];
  for (const d of deals) {
    if (d.stageIdx !== 0) continue; // v1: only 'Contract Signed / Pending'
    const close = typeof d.fields["t_closing"] === "string" ? (d.fields["t_closing"] as string) : "";
    const dtc = /\d/.test(close) ? daysDiff(now, close) : null;
    const open = tasks.filter((t) => t.deal === d.address && !isTaskDone(t.status)).length;
    const tone: RiskRow["tone"] = dtc !== null && dtc < 0 ? "overdue" : dtc !== null && dtc <= 7 ? "urgent" : "ok";
    rows.push({ address: d.address, close, dtc, open, tone });
  }
  rows.sort((x, y) => (x.dtc === null ? 99 : x.dtc) - (y.dtc === null ? 99 : y.dtc));
  return rows.slice(0, 6);
}

/** v1's risk pill text: "no date" / "Nd overdue" / "Nd to close". */
export const riskPill = (dtc: number | null): string =>
  dtc === null ? "no date" : dtc < 0 ? Math.abs(dtc) + "d overdue" : dtc + "d to close";

// ---- SMS Outreach (v1 anlLoadOutreach) ------------------------------------------

export interface Outreach {
  sent: number;
  received: number;
  contacts: number;
  activeWeek: number;
  replied: number;
  rate: number; // %
}

/** Compute over the most-recent `cap` messages (v1 pages 1000 — see header). */
export function outreach(comms: Comm[], now: Date, cap = 1000): Outreach {
  const recent = [...comms].sort((a, b) => b.createdAt - a.createdAt).slice(0, cap);
  const sms = recent.filter((r) => !r.channelIsCall);
  const out = sms.filter((r) => r.direction === "out");
  const inn = sms.filter((r) => r.direction === "in");
  const outNums = new Set(out.map((r) => r.peer).filter(Boolean));
  const inNums = new Set(inn.map((r) => r.peer).filter(Boolean));
  let replied = 0;
  outNums.forEach((n) => {
    if (inNums.has(n)) replied++;
  });
  const wk = now.getTime() - 7 * 86400000;
  const active = new Set(sms.filter((r) => r.createdAt >= wk).map((r) => r.peer).filter(Boolean)).size;
  return {
    sent: out.length,
    received: inn.length,
    contacts: outNums.size,
    activeWeek: active,
    replied,
    rate: outNums.size ? Math.round((replied / outNums.size) * 100) : 0,
  };
}

/** v1 rate hues: ≥30 green, ≥15 amber, else red. */
export const rateTone = (rate: number): "good" | "ok" | "bad" => (rate >= 30 ? "good" : rate >= 15 ? "ok" : "bad");

// ---- SVG geometry (v1 _anlArea / _anlDonut) --------------------------------------

export const AREA_W = 560;
export const AREA_H = 160;
const AREA_PAD = 24;

export function areaPoints(months: AnlMonth[], key: "rev" | "exp"): { pts: string; area: string } {
  if (!months.length) return { pts: "", area: "" };
  const max = Math.max(1, ...months.map((m) => Math.max(m.rev, m.exp)));
  const xs = (i: number): number => AREA_PAD + i * ((AREA_W - 2 * AREA_PAD) / Math.max(months.length - 1, 1));
  const ys = (v: number): number => AREA_H - AREA_PAD - (v / max) * (AREA_H - 2 * AREA_PAD);
  const pts = months.map((m, i) => `${xs(i)},${ys(m[key])}`);
  const area = `M${pts[0]} L${pts.join(" L")} L${xs(months.length - 1)},${AREA_H - AREA_PAD} L${xs(0)},${AREA_H - AREA_PAD} Z`;
  return { pts: pts.join(" "), area };
}

export const areaX = (i: number, n: number): number => AREA_PAD + i * ((AREA_W - 2 * AREA_PAD) / Math.max(n - 1, 1));

export interface DonutSeg {
  color: string;
  len: number;
  off: number;
}

/** r=54, circumference segments in source order (v1 rotates -90° in SVG). */
export function donutSegs(sources: AnlSource[]): { segs: DonutSeg[]; c: number } {
  const r = 54;
  const c = 2 * Math.PI * r;
  let off = 0;
  const segs = sources.map((s) => {
    const len = (s.pct / 100) * c;
    const seg = { color: s.color, len, off };
    off += len;
    return seg;
  });
  return { segs, c };
}

export const usd = (n: number): string => moneyExact(n);

export const ANL_MSG = {
  title: "Company Analytics",
  sub: "Portfolio performance across every deal, market and team member.",
  outreachCaption: (replied: number, contacts: number): string =>
    `${replied} of ${contacts} contacts you texted replied (all time, last 200 messages)`,
  disclaimer:
    "Profit figures are modeled estimates (ARV − purchase − rehab − selling/buying costs). Expenses are actuals from the Expenses ledger. Connect QuickBooks/Drive backends for fully reconciled reporting.",
  noSms: "Could not load SMS data.",
  noPending: "No pending deals.",
  noMarket: "No market data.",
  noExp: "No expense data.",
};
